import { useMemo, useState, useEffect } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from "@tanstack/react-table";

import { ArrowUpDown, Close } from "@monta/icons";

import type { ColumnDef } from "@tanstack/react-table";
import type { DifficultyRowData, CustomTanStackTableProps } from "./types";

const columnHelper = createColumnHelper<DifficultyRowData>();

const useColumns = (_enableFilters: boolean, isPrint: boolean) =>
  useMemo<ColumnDef<DifficultyRowData, any>[]>(
    () => [
      columnHelper.accessor("topic", {
        header: () => <span>موضوع</span>,
        cell: (info) => (
          <div
            className={`text-right font-medium ${isPrint ? "text-normal" : "text-xs"} text-neutral-700`}
          >
            {info.getValue()}
          </div>
        ),
        enableSorting: false
      }),
      columnHelper.accessor("answerRate", {
        header: () => <span>نرخ پاسخ‌دهی</span>,
        cell: (info) => (
          <div className="text-center">
            <div
              className={`text-center font-medium ${isPrint ? "text-normal" : "text-xs"} text-neutral-600`}
            >
              {info.getValue()}
            </div>
          </div>
        ),
        enableSorting: false
      }),
      columnHelper.accessor("accuracy", {
        header: () => <span className="text-center">دقت پاسخ‌‌دهی</span>,
        cell: (info) => (
          <div
            className={`text-center font-medium ${isPrint ? "text-normal" : "text-xs"} text-neutral-600`}
          >
            {info.getValue()}
          </div>
        )
      }),
      columnHelper.accessor("finalScore", {
        header: () => <span className="text-center">نمره نهایی</span>,
        cell: (info) => (
          <div
            className={`text-center font-medium ${isPrint ? "text-normal" : "text-xs"} text-neutral-600`}
          >
            {info.getValue()}
          </div>
        )
      }),
      columnHelper.accessor("correct", {
        header: () => <span className="text-center">درست</span>,
        cell: (info) => (
          <div
            className={`text-center font-medium ${isPrint ? "text-normal" : "text-xs"} text-[#039855]`}
          >
            {info.getValue()}
          </div>
        )
      }),
      columnHelper.accessor("wrong", {
        header: () => <span className="text-center">غلط</span>,
        cell: (info) => (
          <div
            className={`text-center font-medium ${isPrint ? "text-normal" : "text-xs"} text-[#D92D20]`}
          >
            {info.getValue()}
          </div>
        )
      }),
      columnHelper.accessor("skipped", {
        header: () => <span className="text-center">نزده</span>,
        cell: (info) => (
          <div
            className={`text-center font-medium ${isPrint ? "text-normal" : "text-xs"}`}
          >
            {info.getValue()}
          </div>
        )
      }),
      columnHelper.accessor("total", {
        header: () => <span className="text-center">کل سوالات</span>,
        cell: (info) => (
          <div
            className={`text-center font-medium ${isPrint ? "text-normal" : "text-xs"}`}
          >
            {info.getValue()}
          </div>
        )
      })
    ],
    []
  );

function DifficultyTable({
  data,
  enablePagination = true,
  enableSorting = true,
  enableFilters = false,
  isPrint = false
}: Readonly<CustomTanStackTableProps>) {
  const [guideDismissed, setGuideDismissed] = useState(false);
  useEffect(() => {
    const dismissed =
      localStorage.getItem("exam-result-guide-dismissed") === "true";
    setGuideDismissed(dismissed);
  }, []);

  // Columns are memoized for performance
  const columns = useColumns(enableFilters, isPrint);

  // Create the table instance with relevant plugins
  const table = useReactTable({
    data,
    columns,
    // core row model always needed
    getCoreRowModel: getCoreRowModel(),
    // conditionally enable sorting / filtering / pagination
    getSortedRowModel:
      enableSorting && data.length > 1 ? getSortedRowModel() : undefined,
    getFilteredRowModel: enableFilters ? getFilteredRowModel() : undefined,
    getPaginationRowModel: enablePagination
      ? getPaginationRowModel()
      : undefined,
    // performance: avoid expensive re-rendering by using autoReset flags only when necessary
    autoResetPageIndex: false,
    debugTable: false
  });

  return (
    <div className="max-w-full py-4">
      <div className="overflow-x-auto rounded-3 ">
        <table
          className="min-w-full ![border-collapse:collapse] [border-spacing:0]"
          role="table"
          aria-label="نتایج"
        >
          <thead className="bg-[#FFF5EE] rounded-3">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => {
                  const canSort =
                    enableSorting &&
                    data.length > 1 &&
                    header.column.getCanSort();
                  const sortDirection = header.column.getIsSorted();

                  // Extract nested ternary into a variable for clarity and lint compliance
                  let ariaSort: "ascending" | "descending" | "none" = "none";
                  if (sortDirection === "asc") {
                    ariaSort = "ascending";
                  } else if (sortDirection === "desc") {
                    ariaSort = "descending";
                  }

                  return (
                    <th
                      key={header.id}
                      scope="col"
                      className={`px-2 h-12 ${isPrint ? "text-[18px]" : "text-sm"} font-medium text-neutral-700 ${canSort ? "cursor-pointer" : "cursor-default"}`}
                      aria-sort={ariaSort}
                      onClick={
                        canSort
                          ? header.column.getToggleSortingHandler()
                          : undefined
                      }
                    >
                      <div
                        className={`flex items-center ${index === 0 ? "justify-start" : "justify-center"}`}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {canSort
                          ? (() => {
                              let sortIcon = "";
                              if (sortDirection === "asc") sortIcon = "↑";
                              else if (sortDirection === "desc") sortIcon = "↓";
                              return (
                                <span className="text-xs opacity-70 mr-1">
                                  {sortIcon}
                                </span>
                              );
                            })()
                          : null}
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          <tbody className="![border-collapse:collapse] [border-spacing:0] border border-[#E2E8F0] rounded-b-2">
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="p-6 text-center text-gray-500"
                >
                  ردیفی وجود ندارد
                </td>
              </tr>
            ) : (
              <>
                {!guideDismissed && data.length > 1 && enableSorting && (
                  <tr className="h-10 border-dashed border-b border-neutral-400 bg-[#FCFCFD] relative">
                    <td
                      colSpan={data.length}
                      className="flex items-center justify-between h-10 w-full gap-2 text-[#1D2939] font-medium px-2"
                    >
                      <div className="flex items-center">
                        <ArrowUpDown size={20} className="ml-2" />
                        <div className="flex items-center xl:gap-1 lg:gap-[2px] xl:!flex-row lg:flex-col">
                          <p className="xl:text-xs lg:text-[10px]">
                            برای مرتب سازی روی سرستون‌ها کلیک کنید
                          </p>

                          <span className="xl:text-xs lg:text-[9px]">
                            (برای تغییر جهت، دوباره کلیک کنید )
                          </span>
                        </div>
                      </div>
                    </td>

                    <button
                      className="cursor-pointer absolute top-2/4 -translate-y-1/2 left-6 p-1 rounded-full transition-all duration-200 ease-in-out hover:bg-red-50 hover:scale-110 active:scale-95 active:bg-red-100 group"
                      onClick={() => {
                        setGuideDismissed(true);
                        localStorage.setItem(
                          "exam-result-guide-dismissed",
                          "true"
                        );
                      }}
                    >
                      <Close
                        size={20}
                        color="#1D2939"
                        className="transition-colors duration-200 group-hover:text-red-500 group-active:text-red-600"
                      />
                    </button>
                  </tr>
                )}

                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id} className="h-14 border-b border-[#E2E8F0]">
                    {row.getVisibleCells().map((cell) => (
                      <td dir="ltr" key={cell.id} className="h-full gap-2 px-2">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination controls (conditional) */}
      {enablePagination && (
        <div className="mt-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-sm">
            <button
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              className="px-3 py-1 border rounded disabled:opacity-40"
            >
              قبلی
            </button>
            <button
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              className="px-3 py-1 border rounded disabled:opacity-40"
            >
              بعدی
            </button>

            <span className="ml-2">
              صفحه <strong>{table.getState().pagination.pageIndex + 1}</strong>{" "}
              از <strong>{table.getPageCount()}</strong>
            </span>
          </div>

          <div className="flex items-center gap-2 text-sm">
            <label className="flex items-center gap-2">
              <span>ردیف‌ها:</span>
              <select
                value={table.getState().pagination.pageSize}
                onChange={(e) => table.setPageSize(Number(e.target.value))}
                className="border rounded px-2 py-1"
              >
                {[5, 10, 20, 50].map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
      )}
    </div>
  );
}

export default DifficultyTable;
